import { CSSProperties, MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// import { ExperienceEvents, HostEvents } from 'arise-bridge';
import { useAppDispatch, useAppSelector } from '@/state/store';

import {
    selectInventoryItem,
    setIsNewsTickerVisible,
    setCurrentlyViewingMedia,
    setCurrentlyViewingReward,
    setRewardClaimError,
    setIsInventoryOpen,
    setIsLeaderboardOpen,
    setStartShare,
    setIsLocalePickerPanelVisible,
    loadTranslations,
    incrementIframeReloadCount,
    setDiscordMobileShareUrl,
} from '@/state/features/app';
import { awardBadge, awardTitle, setMissionComplete } from '@/state/features/profile';
import { queueNewsTickerItem, setNewsTickerTag } from '@/state/features/feed';
import { useAriseBridge } from '@/arise/bridge/useAriseBridge';
import { AriseBridgeHost } from '@/arise/bridge/AriseBridgeHost';
import { failedGame, submitFeedUpdate, queueAchievement } from '@/state/features/app';
import LoadingScreen from '../LoadingScreen';
import { sendEventV2, sendEventV2Once } from '@/utils/analytics';
import { copyToClipboard, safeStringify } from '@/utils/dom';
import {
    userEvent,
    getUniverseMetadata,
    getUserCount,
    getNewsTickerData,
    getAuthToken,
    claimReward,
    getRewardStatus,
} from '@/arise/api';
import { homePath } from '@/theme/vars';
import { useNavigateToSignup } from '@/hooks/auth';
import DiscordAuthFlow, { DiscordAuthFlowMethods, QuestUnenrolledNotice } from '@/components/DiscordAuthFlow';
import { HOST_PLATFORM, IS_DISCORD_ACTIVITY } from '@/globals';
import { NewsTickerItemData, NewsTickerItemDataIdOnly } from '@/models/feed';
import { discordPlatform, isDiscordOnMobile, triggerDiscordShare, shareImageInDiscordDesktop } from '@/components/DiscordAuthFlow/useDiscord';
import { Reward, ViewableMedia } from '@/models/misc';
import { isTwitchUrl, isTwitchEmbedSupported } from '@/components/MediaViewer/TwitchPlayer/twitchUtils';
import usePushUserData from '@/components/IframeContainer/usePushUserData';
import { useLocation } from '@/hooks/location';
import { useTheme } from '@/theme';
import { getUITranslatedText } from '@/components/TranslatedText';
import useLeaderboardBridgeEvents from '@/components/IframeContainer/bridge/useLeaderboardBridgeEvents';
import useDiscordBridgeEvents from '@/components/IframeContainer/bridge/useDiscordBridgeEvents';

interface IframeContainerProps {
    src?: string;
    bridgeRef?: MutableRefObject<AriseBridgeHost>;
    arisewareProjectID?: string;
    onReady?: () => void;
    loadingBackgroundImageUrl?: string;
    loadingLogoImageUrl?: string;
    loadingText?: [
        { title: string; subtitle: string },
        { title: string; subtitle: string },
        { title: string; subtitle: string },
    ];
    forceInFrontOfUi?: boolean;
    style?: CSSProperties;
    discordRef: MutableRefObject<DiscordAuthFlowMethods | undefined>;
}

export default function IframeContainer({
    src,
    bridgeRef,
    arisewareProjectID,
    onReady,
    loadingBackgroundImageUrl,
    loadingLogoImageUrl,
    loadingText,
    forceInFrontOfUi,
    style,
    discordRef,
}: IframeContainerProps) {
    const navigate = useNavigate();
    const { locationId } = useParams();
    const [iframeReady, setIframeReady] = useState(false);
    const reloadCount = useAppSelector((state) => state.app.iframeReloadCount);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const locationRef = useRef<any>(null);
    const claimedRewardsRef = useRef<Record<string, Reward>>({});
    const loggedIn = useAppSelector((state) => state.auth.loggedIn);
    const theme = useTheme();

    let iframeUrl = useMemo(() => {
        const url = new URL(window.location.href);
        const iframeUrlParam = url.searchParams.get('iframeUrl');
        if (iframeUrlParam) {
            src = iframeUrlParam;
        }

        const params = new URLSearchParams(window.location.search);
        params.delete('iframeUrl');

        const paramsString = params.toString();
        if (paramsString) {
            src = `${src}?${paramsString}`;
        }
        return src;
    }, [src]);

    // If iframeUrl is passed as a query param, use that instead

    const dispatch = useAppDispatch();

    const bridge = useAriseBridge(iframeRef, {
        arisewareProjectID,
    });
    // Expose bridge to parent
    if (bridgeRef) {
        bridgeRef.current = bridge;
    }

    const navigateToSignup = useNavigateToSignup();

    const status = useAppSelector((state) => state.discord.questStatus);
    const checked = useAppSelector((state) => state.discord.questStatusChecked);
    const questCTAURL = useAppSelector((state) => state.discord.questCTAURL);
    const questCTAURLRef = useRef<string | undefined>(questCTAURL);
    questCTAURLRef.current = questCTAURL;
    locationRef.current = useLocation(locationId || '');

    useEffect(() => {
        bridge?.sendEvent('quest-status-update', { status, checked });
    }, [bridge, status, checked]);

    const { pushUserData, pushLocale, pushTranslations, pushDiscordLocale, pushDiscordPremiumType, pushQuestInfo } =
        usePushUserData(bridge);

    function sendAuthToken() {
        const token = getAuthToken();
        bridge?.sendEvent('arise-token', token);
    }

    function sendCCSettings() {
        // Using ref here because this function is used in a useEffect with no location dependency
        const settings = locationRef.current?.arisewareSettings || undefined;
        bridge?.sendEvent('custom-content-settings', settings);
    }

    /*
     * Initialisation Events
     */
    // Automatically send events when user is logged in
    useEffect(() => {
        if (loggedIn && bridge) {
            sendAuthToken();
        }
    }, [loggedIn, bridge]);

    // Send location custom content settings when they are loaded
    useEffect(() => {
        if (locationRef.current && bridge) {
            sendCCSettings();
        }
    }, [location, bridge]);

    useEffect(() => {
        if (!bridge) return;

        function onReadyHandler(data: any) {
            setIframeReady(data.detail);
            if (data.detail === true) {
                onReady();
            }
        }

        function onFailed() {
            dispatch(failedGame({ arisewareProjectID }));
        }

        function onFeedUpdate(data: any) {
            dispatch(submitFeedUpdate(data.detail));
        }

        function onQueueAchievement(data: any) {
            dispatch(queueAchievement(data.detail));
        }

        function onSetNewsTickerVisibility(data: { detail: boolean }) {
            dispatch(setIsNewsTickerVisible(data.detail));
        }

        function onSetLocalePickerVisibility(data: { detail: boolean }) {
            dispatch(setIsLocalePickerPanelVisible(data.detail));
        }

        function onSetInventoryIsOpen(data: any) {
            dispatch(setIsInventoryOpen(data.detail));
        }

        function onSelectInventoryItem() {
            dispatch(selectInventoryItem());
        }

        function onTrack(data: {
            detail: {
                eventType: string;
                metadata: {
                    strings: string[];
                    numbers: number[];
                };
            };
        }) {
            const { eventType, metadata } = data.detail;
            if (typeof eventType !== 'string') {
                console.error('Cannot send event from iFrame - eventType is invalid or missing:', data.detail);
                return;
            }
            sendEventV2(eventType, metadata?.strings, metadata?.numbers, true);
        }

        function onCheckpoint(data: {
            detail: {
                name: string;
                time: number;
                id: number;
            };
        }) {
            const { name, time, id } = data.detail;
            sendEventV2('checkpoint', [name], [time, id]);
        }

        function onGotoHome() {
            navigate(homePath);
        }

        function onGotoSignup() {
            navigateToSignup();
        }

        function onGoto(data: { detail: string }) {
            navigate(`${data.detail}`);
        }

        function onViewMedia(data: { detail: ViewableMedia }) {
            const { resourceUrl } = data.detail;
            // If twitch embed not supported open as url instead
            if (resourceUrl && isTwitchUrl(resourceUrl) && !isTwitchEmbedSupported()) {
                sendEventV2('external_link_opened', [resourceUrl]);
                if (IS_DISCORD_ACTIVITY && discordRef.current) {
                    discordRef.current.openExternalLink(resourceUrl);
                } else {
                    window.open(resourceUrl, '_blank');
                }
                return;
            }
            dispatch(setCurrentlyViewingMedia(data.detail));
        }

        function onViewReward(data: { detail: { id: string } }) {
            if (!data.detail?.id) {
                console.log('[Arise] view-reward failed: missing id');
                return;
            }
            const reward = claimedRewardsRef.current[data.detail.id];
            if (reward) {
                dispatch(setCurrentlyViewingReward(reward));
            } else {
                console.log('[Arise] view-reward: no claimed reward found for id', data.detail.id);
            }
        }

        async function onClaimReward(data: { detail: { promotionId: string } }) {
            try {
                if (!data.detail?.promotionId) {
                    console.log('[Arise] claim-reward failed: missing promotionId');
                    return;
                }
                const result = await claimReward(data.detail.promotionId);
                if (result.reward) {
                    claimedRewardsRef.current[result.reward.id] = result.reward;
                    sendEventV2('reward__claim_success', [result.reward.name, data.detail.promotionId]);
                } else {
                    sendEventV2('reward__claim_failed', [data.detail.promotionId, result?.status]);
                }
                bridge?.sendEvent('claim-reward-response', result);
            } catch (error) {
                sendEventV2('reward__claim_failed', [data.detail.promotionId]);
                console.log('[Arise] claim-reward failed', error);
            }
        }

        async function onClaimAndViewReward(data: { detail: { promotionId: string } }) {
            if (!data.detail?.promotionId) {
                console.log('[Arise] claim-and-view-reward failed: missing promotionId');
                return;
            }
            try {
                const result = await claimReward(data.detail.promotionId);
                bridge?.sendEvent('claim-and-view-reward-response', result);
                switch (result.status) {
                    case 'win':
                        if (result.reward) {
                            claimedRewardsRef.current[result.reward.id] = result.reward;
                            sendEventV2('reward__claim_success', [result.reward.name, data.detail.promotionId]);
                            dispatch(setCurrentlyViewingReward(result.reward));
                        } else {
                            dispatch(setRewardClaimError({ code: 'GENERIC_ERROR' }));
                            sendEventV2('reward__claim_failed', [data.detail.promotionId, result.status]);
                        }
                        break;
                    case 'already_claimed':
                        if (result.reward) {
                            claimedRewardsRef.current[result.reward.id] = result.reward;
                            dispatch(setCurrentlyViewingReward(result.reward));
                        } else {
                            dispatch(setRewardClaimError({ code: 'GENERIC_ERROR' }));
                            sendEventV2('reward__claim_failed', [data.detail.promotionId, result.status]);
                        }
                        break;
                    case 'in_progress':
                        console.log('[Arise] claim-and-view-reward: claim already in_progress, ignoring');
                        sendEventV2('reward__claim_failed', [data.detail.promotionId, result.status]);
                        break;
                    default:
                        dispatch(setRewardClaimError({ code: result.status }));
                        sendEventV2('reward__claim_failed', [data.detail.promotionId, result.status]);
                        break;
                }
            } catch (error) {
                sendEventV2('reward__claim_failed', [data.detail.promotionId]);
                console.log('[Arise] claim-and-view-reward failed', error);
                const code = error instanceof Error ? error.message : 'GENERIC_ERROR';
                dispatch(setRewardClaimError({ code }));
            }
        }

        async function onGetRewardStatus(data: { detail: { promotionId: string } }) {
            try {
                if (!data.detail?.promotionId) {
                    console.log('[Arise] get-reward-status failed: missing promotionId');
                    return;
                }
                const result = await getRewardStatus(data.detail.promotionId);
                bridge?.sendEvent('get-reward-status-response', result);
            } catch (error) {
                console.log('[Arise] get-reward-status failed', error);
            }
        }

        function onCopyText(data: { detail: string }) {
            copyToClipboard(data.detail);
        }

        function onOpenUrl(data: { detail: string }) {
            sendEventV2('external_link_opened', [data.detail]);
            if (IS_DISCORD_ACTIVITY && discordRef.current) {
                discordRef.current.openExternalLink(data.detail);
            } else {
                window.open(data.detail, '_blank');
            }
        }

        function onOpenQuestCTA(data: { detail: string }) {
            const url = questCTAURLRef.current || data.detail;
            sendEventV2('external_link_opened', [url]);
            if (IS_DISCORD_ACTIVITY && discordRef.current) {
                discordRef.current.openExternalLink(url);
            } else {
                window.open(url, '_blank');
            }
        }

        function onQuestCompleted() {
            if (IS_DISCORD_ACTIVITY && discordRef.current) {
                discordRef.current.completeQuest();
            }
            dispatch(setMissionComplete());
        }

        function onQuestProgress(data: { detail: number }) {
            if (IS_DISCORD_ACTIVITY && discordRef.current) {
                discordRef.current.setQuestProgress(data.detail);
            }
        }

        async function onBotRegister() {
            if (IS_DISCORD_ACTIVITY && discordRef.current) {
                try {
                    await discordRef.current.botRegister();
                    sendEventV2Once('bot-register');
                    bridge?.sendEvent('bot-register-response', { status: 'ok' });
                } catch (error) {
                    console.log('[Arise] bot reg error', error);
                    sendEventV2Once('bot-register-failed', [safeStringify(error)]);
                    bridge?.sendEvent('bot-register-response', { status: 'error' });
                }
            }
        }

        async function onSendUserEvent(data: { detail: NewsTickerItemData }) {
            const isNewsTickerDataIdOnlyItem = (item: NewsTickerItemData): item is NewsTickerItemDataIdOnly => {
                return 'id' in item;
            };

            const item = data?.detail;
            if (item) {
                dispatch(queueNewsTickerItem(item));
                if (isNewsTickerDataIdOnlyItem(item)) {
                    // If is ID only item, send to backend too
                    try {
                        await userEvent(locationId!, item.id);
                    } catch (error) {
                        console.error('Bridge event sendUserEvent could not send event:', error);
                    }
                }
            }
        }

        function onSetNewsTickerTag(data: { detail: string }) {
            dispatch(setNewsTickerTag(data.detail));
        }

        function onShare(data: { detail?: { message?: string; disableAutoLink?: boolean; linkId?: string } }) {
            // If no custom message passed in, use the share text from the CMS
            const fallbackCmsShareText = theme.metadata?.customJSON?.share?.text;
            const translation_shareText = getUITranslatedText('informationScreen_shareText', fallbackCmsShareText);
            const { message = translation_shareText, disableAutoLink, linkId } = data.detail ?? {};
            if (IS_DISCORD_ACTIVITY) {
                triggerDiscordShare(message, disableAutoLink, linkId);
            } else {
                dispatch(setStartShare({ message, disableAutoLink }));
            }
        }

        async function onShareCustomImage(data: {
            detail: { imageUrl: string; filename?: string };
        }) {
            const { imageUrl, filename = 'share.png' } = data.detail ?? {};
            if (!imageUrl) {
                console.log('[Arise] share-custom-image failed: missing imageUrl');
                return;
            }
            try {
                if (IS_DISCORD_ACTIVITY) {
                    if (isDiscordOnMobile()) {
                        dispatch(setDiscordMobileShareUrl({ imageUrl, filename }));
                        sendEventV2('share_custom_image', ['discord']);
                    } else {
                        await shareImageInDiscordDesktop(imageUrl, filename);
                        sendEventV2('share_custom_image', ['discord']);
                    }
                } else {
                    sendEventV2('share_custom_image', ['web']);
                    const openUrl = imageUrl.startsWith('data:')
                        ? URL.createObjectURL(await (await fetch(imageUrl)).blob())
                        : imageUrl;
                    window.open(openUrl, '_blank', 'noopener');
                }
            } catch (error) {
                console.log('[Arise] share-custom-image failed', error);
            }
        }

        function onAwardBadge(data: { detail: string }) {
            const badgeRank = data.detail;
            dispatch(awardBadge(badgeRank));
        }

        function onAwardTitle(data: { detail: string }) {
            const titleId = data.detail;
            dispatch(awardTitle(titleId));
        }

        async function onGetQuestStatus() {
            if (IS_DISCORD_ACTIVITY && discordRef.current) {
                const status = await discordRef.current.getQuestStatus();
                bridge?.sendEvent('quest-status-update', status);
            }
        }

        async function onGetUniverseMetadata() {
            try {
                const json = await getUniverseMetadata();
                bridge?.sendEvent('universe-metadata', json);
            } catch (error) {
                console.log('[Arise] Error cannot getUniverseMetadata');
            }
        }

        function onGetTranslations() {
            pushTranslations();
        }

        function onRefreshTranslations() {
            dispatch(loadTranslations());
        }

        function onGetLocale() {
            pushLocale();
        }

        function onGetDiscordLocale() {
            pushDiscordLocale();
        }

        function onGetDiscordPremiumType() {
            pushDiscordPremiumType();
        }

        function onGetQuestInfo() {
            pushQuestInfo();
        }

        async function onGetNewsTickerData() {
            try {
                const json = await getNewsTickerData();
                bridge?.sendEvent('news-ticker-data', json);
            } catch (error) {
                console.log('[Arise] Error cannot getNewsTickerData');
            }
        }

        function pushDiscordPlatform() {
            if (IS_DISCORD_ACTIVITY) {
                const platform = discordPlatform();
                bridge?.sendEvent('discord-platform', platform);
            }
        }

        function onGetAuthToken() {
            sendAuthToken();
        }

        function getGetCCSettings() {
            sendCCSettings();
        }

        function onGetHostPlatform() {
            bridge?.sendEvent('host-platform', HOST_PLATFORM);
        }

        async function onGetPlayerCount() {
            try {
                const { count } = await getUserCount();
                bridge?.sendEvent('player-count', { count });
            } catch (error) {
                console.log('[Arise] Error cannot getUserCount');
                bridge?.sendEvent('player-count', { count: null });
            }
        }

        function onArisewareReload() {
            dispatch(incrementIframeReloadCount());
        }

        bridge.on('ready', onReadyHandler);
        bridge.on('ready', pushUserData);
        bridge.on('ready', pushDiscordPlatform);
        bridge.on('failed', onFailed);
        bridge.on('feed-update', onFeedUpdate);
        bridge.on('queue-achievement', onQueueAchievement);
        bridge.on('set-news-ticker-visibility', onSetNewsTickerVisibility);
        bridge.on('set-locale-picker-visibility', onSetLocalePickerVisibility);
        bridge.on('set-inventory-is-open', onSetInventoryIsOpen);
        bridge.on('select-inventory-item', onSelectInventoryItem);
        bridge.on('track', onTrack);
        bridge.on('track:checkpoint', onCheckpoint);
        bridge.on('goto_home', onGotoHome);
        bridge.on('goto_signup', onGotoSignup);
        bridge.on('goto', onGoto);
        bridge.on('view-media', onViewMedia);
        bridge.on('view-reward', onViewReward);
        bridge.on('claim-reward', onClaimReward);
        bridge.on('claim-and-view-reward', onClaimAndViewReward);
        bridge.on('get-reward-status', onGetRewardStatus);
        bridge.on('copy-text', onCopyText);
        bridge.on('send-user-event', onSendUserEvent);
        bridge.on('news-ticker-tag', onSetNewsTickerTag);
        bridge.on('share', onShare);
        bridge.on('share-custom-image', onShareCustomImage);
        bridge.on('open-url', onOpenUrl);
        bridge.on('open-quest-cta', onOpenQuestCTA);
        bridge.on('quest-completed', onQuestCompleted);
        bridge.on('quest-progress', onQuestProgress);
        bridge.on('get-quest-status', onGetQuestStatus);
        bridge.on('bot-register', onBotRegister);
        bridge.on('get-universe-metadata', onGetUniverseMetadata);
        bridge.on('get-news-ticker-data', onGetNewsTickerData);
        bridge.on('get-translations', onGetTranslations);
        bridge.on('refresh-translations', onRefreshTranslations);
        bridge.on('get-locale', onGetLocale);
        bridge.on('get-discord-locale', onGetDiscordLocale);
        bridge.on('get-discord-premium-type', onGetDiscordPremiumType);
        bridge.on('get-quest-info', onGetQuestInfo);
        bridge.on('award-badge', onAwardBadge);
        bridge.on('award-title', onAwardTitle);
        bridge.on('get-arise-token', onGetAuthToken);
        bridge.on('get-custom-content-settings', getGetCCSettings);
        bridge.on('get-host-platform', onGetHostPlatform);
        bridge.on('get-player-count', onGetPlayerCount);
        bridge.on('ariseware-reload', onArisewareReload);

        return () => {
            dispatch(setIsNewsTickerVisible(true));
            bridge.off('ready', onReady);
            bridge.off('ready', pushUserData);
            bridge.off('failed', onFailed);
            bridge.off('feed-update', onFeedUpdate);
            bridge.off('queue-achievement', onQueueAchievement);
            bridge.off('set-news-ticker-visibility', onSetNewsTickerVisibility);
            bridge.off('set-inventory-is-open', onSetInventoryIsOpen);

            bridge.off('select-inventory-item', onSelectInventoryItem);
            bridge.off('track', onTrack);
            bridge.off('track:checkpoint', onCheckpoint);
            bridge.off('goto_home', onGotoHome);
            bridge.off('goto_signup', onGotoSignup);
            bridge.off('goto', onGoto);
            bridge.off('view-media', onViewMedia);
            bridge.off('view-reward', onViewReward);
            bridge.off('claim-reward', onClaimReward);
            bridge.off('claim-and-view-reward', onClaimAndViewReward);
            bridge.off('get-reward-status', onGetRewardStatus);
            bridge.off('copy-text', onCopyText);
            bridge.off('send-user-event', onSendUserEvent);
            bridge.off('news-ticker-tag', onSetNewsTickerTag);
            bridge.off('share', onShare);
            bridge.off('share-custom-image', onShareCustomImage);
            bridge.off('open-url', onOpenUrl);
            bridge.off('open-quest-cta', onOpenQuestCTA);
            bridge.off('quest-completed', onQuestCompleted);
            bridge.off('quest-progress', onQuestProgress);
            bridge.off('get-quest-status', onGetQuestStatus);
            bridge.off('bot-register', onBotRegister);
            bridge.off('get-universe-metadata', onGetUniverseMetadata);
            bridge.off('get-news-ticker-data', onGetNewsTickerData);
            bridge.off('get-translations', onGetTranslations);
            bridge.off('refresh-translations', onRefreshTranslations);
            bridge.off('get-locale', onGetLocale);
            bridge.off('get-discord-locale', onGetDiscordLocale);
            bridge.off('get-discord-premium-type', onGetDiscordPremiumType);
            bridge.off('get-quest-info', onGetQuestInfo);
            bridge.off('award-badge', onAwardBadge);
            bridge.off('award-title', onAwardTitle);
            bridge.off('get-arise-token', onGetAuthToken);
            bridge.off('get-custom-content-settings', getGetCCSettings);
            bridge.off('get-host-platform', onGetHostPlatform);
            bridge.off('get-player-count', onGetPlayerCount);
            bridge.off('ariseware-reload', onArisewareReload);
            bridge.off('set-locale-picker-visibility', onSetLocalePickerVisibility);
        };
    }, [bridge, iframeUrl]);

    useEffect(() => {
        setIframeReady(false);
    }, [reloadCount]);

    useLeaderboardBridgeEvents(bridge);
    useDiscordBridgeEvents(bridge);

    const forceInFrontOfUIStyle: React.CSSProperties = forceInFrontOfUi ? { position: 'absolute', zIndex: 100000 } : {};

    return (
        <>
            {iframeUrl?.startsWith('null') ? (
                <></>
            ) : (
                <iframe
                    key={reloadCount}
                    allow="xr-spatial-tracking; autoplay; encrypted-media;"
                    ref={iframeRef}
                    src={iframeUrl}
                    style={{
                        width: '100%',
                        height: '100%',
                        opacity: iframeReady ? 1 : 0,
                        ...style,
                        ...forceInFrontOfUIStyle,
                    }}
                />
            )}
            <LoadingScreen
                show={!iframeReady}
                backgroundImageUrl={loadingBackgroundImageUrl}
                logoImageUrl={loadingLogoImageUrl}
                loadingText={loadingText}
                style={forceInFrontOfUIStyle}
            />
        </>
    );
}
